// RecalibrationCountdown — 5-state indicator per research §7.1.
// Visual states: upcoming (>7d), due_soon (≤7d), ready (day-of),
// overdue (1–14d), reanchor (14d+).
//
// Copy deliberately calm: no red, no urgency.

import { useEffect } from 'react';
import type { RecalibrationStatus } from '@shared/types';
import { useRecalibrationStore } from '@shared/state';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';

interface Props {
  onOpenWizard?: () => void;
  compact?: boolean;
}

interface StateStyle {
  label: string;
  bg: string;
  border: string;
  fg: string;
  dotColor: string;
  pulse: boolean;
}

function styleFor(status: RecalibrationStatus, daysUntil: number): StateStyle {
  switch (status) {
    case 'upcoming':
      return {
        label: `Next Recalibration in ${daysUntil} days`,
        bg: 'rgba(108,92,231,0.06)',
        border: 'rgba(108,92,231,0.18)',
        fg: '#c0bdd4',
        dotColor: '#6C5CE7',
        pulse: false,
      };
    case 'due_soon':
      return {
        label:
          daysUntil === 1
            ? 'Recalibration tomorrow'
            : `Recalibration in ${daysUntil} days`,
        bg: 'rgba(240,165,0,0.08)',
        border: 'rgba(240,165,0,0.30)',
        fg: '#F0A500',
        dotColor: '#F0A500',
        pulse: false,
      };
    case 'ready':
      return {
        label: 'Recalibration ready',
        bg: 'rgba(240,165,0,0.12)',
        border: 'rgba(240,165,0,0.45)',
        fg: '#F0A500',
        dotColor: '#F0A500',
        pulse: true,
      };
    case 'overdue':
      return {
        label: 'Recalibration ready — complete when you’re ready',
        bg: 'rgba(108,92,231,0.08)',
        border: 'rgba(108,92,231,0.30)',
        fg: '#c0bdd4',
        dotColor: '#6C5CE7',
        pulse: false,
      };
    case 'reanchor':
      return {
        label: 'Re-anchor your cycle',
        bg: 'rgba(108,92,231,0.12)',
        border: 'rgba(108,92,231,0.45)',
        fg: '#A39BE0',
        dotColor: '#A39BE0',
        pulse: true,
      };
  }
}

export function RecalibrationCountdown({ onOpenWizard, compact }: Props) {
  const { schedule, status, daysUntil, refreshDerivations } =
    useRecalibrationStore();

  useEffect(() => {
    const id = setInterval(() => refreshDerivations(), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, [refreshDerivations]);

  // Telemetry — once per status value: countdown shown, and (if applicable) the
  // soft-reset re-anchor prompt being surfaced.
  useEffect(() => {
    if (!status) return;
    trackEvent({
      name: ANALYTICS_EVENTS.RECAL_COUNTDOWN_VIEWED,
      properties: { ...getBaseProperties(), status, daysUntil: daysUntil ?? 0 },
    });
    if (status === 'reanchor') {
      trackEvent({
        name: ANALYTICS_EVENTS.RECAL_SOFT_RESET_PROMPTED,
        properties: {
          ...getBaseProperties(),
          daysOverdue: daysUntil !== null && daysUntil < 0 ? -daysUntil : 14,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (!schedule || !status || daysUntil === null) return null;

  const style = styleFor(status, daysUntil);
  const interactive = !!onOpenWizard;
  const nextDate = new Date(schedule.nextRecalibration);
  const dateLabel = nextDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  return (
    <button
      type="button"
      onClick={onOpenWizard}
      disabled={!interactive}
      title={`Next scheduled: ${dateLabel}`}
      className={`inline-flex items-center gap-2 rounded-full text-[11px] font-medium transition-all duration-300 ${
        interactive ? 'cursor-pointer hover:-translate-y-px' : 'cursor-default'
      } ${compact ? 'px-2.5 py-1' : 'px-3 py-1.5'}`}
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        color: style.fg,
      }}
    >
      <span
        className={`w-[6px] h-[6px] rounded-full ${
          style.pulse ? 'animate-dashboard-pulse-gold' : ''
        }`}
        style={{ background: style.dotColor }}
      />
      <span>{style.label}</span>
    </button>
  );
}
